import { useMemo } from 'react';
import { useApi } from '../../hooks/useApi';
import { Th } from '../../utils/api';
import { LineChart, StackedBar, ChartCard, ChartLoader, ChartError } from '../../components/Charts';
import { MultiFilterSelect } from '../../components/MultiFilterSelect';
import { APP_COLORS, ALPHA_COLORS, ALPHA_LABELS, appColor, FilterSelect, useThFilters } from './thShared';

const CHART_H = 300;

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { weekday: 'short', month: 'short', day: 'numeric' });
}

export default function ThDailyView({ vertical = 'food' }) {
  const {
    filters, filtersLoaded,
    appNames, setAppNames,
    alphas, setAlphas,
    province, handleProvinceChange,
    district, handleDistrictChange,
    category, setCategory,
    week, setWeek,
    availableDistricts,
  } = useThFilters(vertical);

  const params = useMemo(() => ({
    vertical,
    app_name: appNames,
    alpha:    alphas,
    province,
    district,
    category,
    ...(week && { week }),
  }), [vertical, appNames, alphas, province, district, category, week]);

  const { data, loading, error } = useApi(
    () => Th.daily(vertical, {
      app_name: appNames, alpha: alphas, province, district, category,
      ...(week && { week }),
    }),
    params,
    filtersLoaded,
  );

  const dates          = useMemo(() => [...new Set((data || []).map(r => r.date))].sort(), [data]);
  const appNamesInData = useMemo(() => [...new Set((data || []).map(r => r.app_name))].sort(), [data]);
  const xLabels        = useMemo(() => dates.map(fmtDate), [dates]);

  function buildSeries(valueKey) {
    return appNamesInData.map((app, i) => {
      const byDate = Object.fromEntries(
        (data || []).filter(r => r.app_name === app).map(r => [r.date, r[valueKey]])
      );
      return { name: app, color: appColor(app, i), data: dates.map(d => byDate[d] ?? null) };
    });
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const userSeries    = useMemo(() => buildSeries('unique_visitors'), [data, appNamesInData, dates]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const sessionSeries = useMemo(() => buildSeries('total_sessions'),  [data, appNamesInData, dates]);

  const empty = <div className="empty-state">No data for selected filters</div>;

  return (
    <div>
      <div className="filters-bar" style={{ alignItems: 'flex-start' }}>
        <FilterSelect label="Category" value={category} onChange={setCategory} options={filters.categories} />
        <MultiFilterSelect
          label="Alpha"
          values={alphas}
          onChange={setAlphas}
          options={filters.alphas}
          chipColor={v => ALPHA_COLORS[v] || '#6366f1'}
          labelMap={ALPHA_LABELS}
        />
        <MultiFilterSelect
          label="App"
          values={appNames}
          onChange={setAppNames}
          options={filters.app_names}
          chipColor={v => APP_COLORS[v] || '#6366f1'}
        />
        <FilterSelect label="Province" value={province} onChange={handleProvinceChange} options={filters.provinces} />
        <FilterSelect label="District" value={district} onChange={handleDistrictChange} options={availableDistricts} />
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span className="filter-label">Week</span>
          <select
            className="filter-select"
            value={week}
            onChange={e => setWeek(e.target.value)}
            style={{ minWidth: 110 }}
          >
            {filters.weeks.map(w => <option key={w} value={w}>{w}</option>)}
          </select>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

        {/* Row 1 — Market share stacked bars */}
        <ChartCard title="Market Share — Unique Visitors" sub={`${category} · daily % share by app`}>
          {loading && <ChartLoader height={CHART_H} />}
          {error   && <ChartError message={error} height={CHART_H} />}
          {!loading && !error && (userSeries.length > 0
            ? <StackedBar categories={xLabels} series={userSeries} height={CHART_H} normalize />
            : empty)}
        </ChartCard>

        <ChartCard title="Market Share — Sessions" sub={`${category} · daily % share by app`}>
          {loading && <ChartLoader height={CHART_H} />}
          {error   && <ChartError message={error} height={CHART_H} />}
          {!loading && !error && (sessionSeries.length > 0
            ? <StackedBar categories={xLabels} series={sessionSeries} height={CHART_H} normalize />
            : empty)}
        </ChartCard>

        {/* Row 2 — Absolute line charts */}
        <ChartCard title="Daily Unique Visitors — by App" sub={`${category} · all days`}>
          {loading && <ChartLoader height={CHART_H} />}
          {error   && <ChartError message={error} height={CHART_H} />}
          {!loading && !error && (userSeries.length > 0
            ? <LineChart categories={xLabels} series={userSeries} height={CHART_H} />
            : empty)}
        </ChartCard>

        <ChartCard title="Daily Sessions — by App" sub={`${category} · all days`}>
          {loading && <ChartLoader height={CHART_H} />}
          {error   && <ChartError message={error} height={CHART_H} />}
          {!loading && !error && (sessionSeries.length > 0
            ? <LineChart categories={xLabels} series={sessionSeries} height={CHART_H} />
            : empty)}
        </ChartCard>

      </div>
    </div>
  );
}
